import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { parseNoticeText } from '@cloudsign/shared';
import type { NoticeInline } from '@cloudsign/shared';

/** Draws notice text (the shared parseNoticeText blocks) as React elements, never as HTML strings. */
function Inlines({ inlines }: { inlines: NoticeInline[] }) {
  return (
    <>
      {inlines.map((i, n) => {
        let node: ReactNode = i.text;
        if (i.bold) node = <strong>{node}</strong>;
        if (i.italic) node = <em>{node}</em>;
        // Links are shown as text followed by the address; the kiosk (and this preview) never open them.
        if (i.href) node = <span>{i.text} ({i.href})</span>;
        return <Fragment key={n}>{node}</Fragment>;
      })}
    </>
  );
}

export function NoticeView({ body }: { body: string }) {
  const blocks = parseNoticeText(body);
  return (
    <div className="space-y-3 text-sm text-slate-800">
      {blocks.map((b, n) => {
        if (b.type === 'heading') {
          const H = b.level === 1 ? 'h3' : 'h4';
          return (
            <H key={n} className={b.level === 1 ? 'text-lg font-bold' : 'text-base font-semibold'}>
              <Inlines inlines={b.inlines} />
            </H>
          );
        }
        if (b.type === 'list') {
          const L = b.ordered ? 'ol' : 'ul';
          return (
            <L key={n} className={`ml-5 ${b.ordered ? 'list-decimal' : 'list-disc'}`}>
              {b.items.map((it, k) => (
                <li key={k}>
                  <Inlines inlines={it} />
                </li>
              ))}
            </L>
          );
        }
        return (
          <p key={n} className="whitespace-pre-line">
            <Inlines inlines={b.inlines} />
          </p>
        );
      })}
    </div>
  );
}
